import type { SubscriptionPriceProduct, SubscriptionType } from '@/models';
import { findSubscriptionProduct, getSubscriptionTypeLabel } from '@/services/subscription-products';
import type { BillingPeriod } from '@/components/landing/PublicPricingCard';

/**
 * Overrides für Paket-Texte je Abrechnungszeitraum (1m/6m/12m). Alle Felder
 * optional: nicht gesetzte Felder fallen auf die Basiswerte des Pakets zurück.
 */
export interface LandingPackagePeriodCopy {
  headline?: string;
  hookLines?: string[];
  transitionLine?: string;
  features?: string[];
  tagline?: string;
  scheduleInfo?: string;
  badgeLabel?: string;
  appendSavingsFeature?: boolean;
}

export interface LandingPackageConfig {
  productType: string;
  name: string;
  label: string;
  accentColor: 'green' | 'blue' | 'purple' | 'red';
  headline: string;
  hookLines: string[];
  transitionLine: string;
  features: string[];
  tagline: string;
  ctaLabel: string;
  popular?: boolean;
  separate?: boolean;
  scheduleInfo?: string;
  warnings?: string[];
  periodCopy?: Partial<Record<BillingPeriod, LandingPackagePeriodCopy>>;
}

export const LANDING_PACKAGES: LandingPackageConfig[] = [
  {
    productType: 'SWINGTRADES_LONGTERM',
    name: 'Trade',
    label: 'Aktiv handeln',
    accentColor: 'blue',
    headline: 'Für alle, die aktiv handeln und Bewegungen gezielt nutzen wollen',
    hookLines: [
      'Du investierst bereits – willst aber mehr als nur halten und hoffen.',
      'Du suchst klare Setups, um Chancen im Markt sauber zu nutzen.',
    ],
    transitionLine: 'Genau dafür ist TRADE gemacht.',
    features: [
      'Langfristige Einstiegsideen in Einzelaktien und ETFs',
      'Konkrete Swing-Trades mit klaren Ein- und Ausstiegen',
      'Regelmäßige Q&A Videos',
    ],
    tagline: 'Mehr Bewegung, mehr Möglichkeiten',
    ctaLabel: 'Ich will aktiv handeln',
  },
  {
    productType: 'ALL',
    name: 'Cashflow',
    label: 'Einnahmen aufbauen',
    accentColor: 'purple',
    headline: 'Für alle, die regelmäßige Einnahmen aus ihrem Depot wollen',
    hookLines: [
      'Dein Depot ist aufgebaut – jetzt soll es dich auch bezahlen.',
      'Statt nur auf Kursgewinne zu hoffen, willst du planbaren Cashflow.',
    ],
    transitionLine: 'Genau dafür ist CASHFLOW gemacht.',
    features: [
      'Wöchentliche Cashflow-Setups und Stillhalterstrategien',
      'Gezielte Earnings-Trades, Swing-Trades und Langzeit-Einstiege',
      'Regelmäßige Q&A Videos',
    ],
    tagline: 'Regelmäßige Einnahmen statt nur Buchgewinne',
    ctaLabel: 'Ich will Cashflow erzeugen',
    popular: true,
  },
  {
    productType: 'DAYTRADING',
    name: 'Daytrading',
    label: 'Daytrading',
    accentColor: 'red',
    headline: 'Für alle, die den Markt live und in Echtzeit handeln wollen',
    hookLines: [
      'Du willst sehen, wie an der Börse wirklich gehandelt wird.',
      'Nicht in der Theorie – sondern live, wenn die Setups passieren.',
    ],
    transitionLine: 'Genau dafür ist DAYTRADING gemacht.',
    features: [
      'Börsentägliche Live-Session zum US-Opening (15:15–16:15 Uhr)',
      'Echte Tradingideen, echte Entscheidungen, echte Reaktionen',
      'Setups, Einstiege und Risikomanagement live nachvollziehen',
    ],
    tagline: 'Du bist dabei, wenn es passiert',
    ctaLabel: 'Ich will live dabei sein',
    separate: true,
    scheduleInfo: 'täglich 15:15 – 16:15 Uhr',
    periodCopy: {
      '1m': {
        headline: 'Zum Reinschnuppern',
        hookLines: [
          'Du willst erst sehen, ob Format und Uhrzeit zu deinem Alltag passen? Fair.',
          'Steig einen Monat ein und entscheide dann.',
        ],
        features: [
          'Börsentägliche Live-Session zum US-Opening (15:15–16:15 Uhr)',
          'Echte Tradingideen, echte Entscheidungen, echte Reaktionen',
          'Setups, Einstiege und Risikomanagement live nachvollziehen',
        ],
      },
      '6m': {
        headline: 'Für alle, die es ernst meinen',
        hookLines: [
          'Daytrading lernst du nicht in vier Wochen. Wer den Prozess wirklich verinnerlichen will, braucht Marktphasen: ruhige Wochen, volatile Wochen, Trendwechsel.',
          'Deshalb empfehle ich 6 Monate.',
        ],
        features: [
          'Alles aus dem Monatspaket',
          'Genug Zeit, um den Prozess durch verschiedene Marktphasen zu erleben',
        ],
        appendSavingsFeature: true,
        badgeLabel: 'Empfohlen',
      },
    },
  },
];

/** Laufzeit eines Pakets in Monaten, z.B. '6m' → 6. */
export function getBillingPeriodMonths(billingPeriod: BillingPeriod): number {
  return parseInt(billingPeriod, 10);
}

/**
 * Erste Zeile von {@link getPlanDisplayName} — für Überschriften und Fließtext, in denen der
 * mehrzeilige Paket-Text unleserlich wäre.
 */
export function getPlanShortName(products: SubscriptionPriceProduct[], type: SubscriptionType): string {
  return getPlanDisplayName(products, type).split('\n')[0];
}

export function getPlanDisplayName(products: SubscriptionPriceProduct[], type: SubscriptionType): string {
  const product = findSubscriptionProduct(products, type);

  if (product?.productType === 'WORKSHOP_01') {
    return 'Lukrative Aktie'; // Sven-Special-Case, siehe Registrierung
  }

  // Anzeigetext (Name, Zugangsdauer, Zahlungsintervall) kommt mehrzeilig aus dem Backend
  // (localDescription, Zeilen per "\n"). Die generische Laufzeit "(1M)" wäre hier irreführend –
  // Community 1M ist z.B. 12 Monate Zugang bei monatlicher Zahlung.
  if (product !== undefined && product.localDescription !== '') {
    return product.localDescription;
  }

  return getSubscriptionTypeLabel(product, type);
}
